import _extends from "@babel/runtime/helpers/extends";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
var _excluded = ["align", "bold", "children", "chronicle", "className", "color", "component", "error", "fontFamily", "gotham", "inline", "italic", "light", "size", "style", "uppercase"];
import "core-js/modules/es6.number.constructor.js";
import "core-js/modules/es6.string.bold.js";
// this is the raw web typography implementation
import React from 'react';
import baseStyles, { mergeStyles } from '@nerdwallet/base-styles/react';
import { useDeprecatedPropsWarning } from '@nerdwallet/deprecate';
import { TypographyContextProvider, useTypographyContext } from './TypographyContext';
import typographyContextDefaults from './typography-context-defaults';
import { useTypeStyles } from './useTypeStyles';

var getSizeRuleName = function getSizeRuleName(_ref) {
  var size = _ref.size;
  return "type-".concat(size);
};

var getFamilyRuleName = function getFamilyRuleName(_ref2) {
  var chronicle = _ref2.chronicle,
      fontFamily = _ref2.fontFamily,
      gotham = _ref2.gotham,
      size = _ref2.size;
  var isChronicle = fontFamily === 'chronicle' || chronicle;
  var isGotham = fontFamily === 'gotham' || gotham;
  var isGothamMedium = fontFamily === 'gotham-medium';

  if (isChronicle) {
    if (Number(size) >= 3) {
      return 'chronicle-display';
    }

    return 'chronicle';
  }

  if (isGotham) {
    return 'gotham';
  }

  if (isGothamMedium) {
    return 'gotham-medium';
  }

  return null;
};

var getAlignRuleName = function getAlignRuleName(_ref3) {
  var align = _ref3.align;

  if (align === 'left') {
    return 'text-align-left';
  }

  if (align === 'center') {
    return 'text-align-center';
  }

  if (align === 'right') {
    return 'text-align-right';
  }

  return null;
};
/**
 * Returns a design token from the design system (eg. color-red) based
 * on props. Note the order of precedence,
 * eg. color takes precedence over error, error over light, etc.
 * @returns the color name or null
 */


var getColor = function getColor(_ref4) {
  var error = _ref4.error,
      color = _ref4.color,
      light = _ref4.light;

  if (color) {
    return "color-".concat(color);
  }

  if (error) {
    return 'color-red-dark';
  }

  if (light) {
    return 'color-neutral-darker';
  }

  return null;
};

var shouldUnbold = function shouldUnbold(_ref5) {
  var bold = _ref5.bold,
      component = _ref5.component,
      fontFamily = _ref5.fontFamily;

  // The "Gotham Medium" text style already sets
  // a specific font-weight: 500 -- we do
  // not want to reset its weight if we're applying it
  if (fontFamily === 'gotham-medium') {
    return false;
  }

  return bold === false || !bold && typeof component === 'string' && /^h[1-6]$/i.test(component);
};

export var TypeImpl = /*#__PURE__*/React.forwardRef(function (_ref6, ref) {
  var _ref7;

  var align = _ref6.align,
      boldProp = _ref6.bold,
      children = _ref6.children,
      chronicle = _ref6.chronicle,
      className = _ref6.className,
      color = _ref6.color,
      component = _ref6.component,
      error = _ref6.error,
      fontFamilyProp = _ref6.fontFamily,
      gotham = _ref6.gotham,
      inlineProp = _ref6.inline,
      italic = _ref6.italic,
      light = _ref6.light,
      sizeProp = _ref6.size,
      style = _ref6.style,
      uppercase = _ref6.uppercase,
      otherProps = _objectWithoutProperties(_ref6, _excluded);

  // TODO: remove props and this warning in Currency v3
  useDeprecatedPropsWarning({
    component: 'Type',
    deprecatedProps: [{
      deprecated: 'chronicle',
      version: '2.4.0',
      migration: 'Use the fontFamily prop instead.'
    }, {
      deprecated: 'gotham',
      version: '2.4.0',
      migration: 'Use the fontFamily prop instead.'
    }],
    props: {
      gotham: gotham,
      chronicle: chronicle
    }
  });
  var typographyContext = useTypographyContext() || typographyContextDefaults;
  var inline = inlineProp !== null && inlineProp !== void 0 ? inlineProp : typographyContext === null || typographyContext === void 0 ? void 0 : typographyContext.inline;
  var bold = boldProp !== null && boldProp !== void 0 ? boldProp : typographyContext === null || typographyContext === void 0 ? void 0 : typographyContext.bold;
  var size = sizeProp !== null && sizeProp !== void 0 ? sizeProp : typographyContext === null || typographyContext === void 0 ? void 0 : typographyContext.size;
  var fontFamily = fontFamilyProp !== null && fontFamilyProp !== void 0 ? fontFamilyProp : typographyContext === null || typographyContext === void 0 ? void 0 : typographyContext.fontFamily;
  var Component = component || (inline ? 'span' : 'p');
  var sizeRuleName = getSizeRuleName({
    size: size
  });
  var familyRuleName = getFamilyRuleName({
    chronicle: chronicle,
    fontFamily: fontFamily,
    gotham: gotham,
    size: size
  });
  var alignRuleName = getAlignRuleName({
    align: align
  });
  var colorName = getColor({
    color: color,
    error: error,
    light: light
  });
  return /*#__PURE__*/React.createElement(Component, _extends({}, mergeStyles(baseStyles([colorName, familyRuleName, alignRuleName, (_ref7 = {}, _defineProperty(_ref7, sizeRuleName, size != null), _defineProperty(_ref7, "bold", bold), _defineProperty(_ref7, "italic", italic), _defineProperty(_ref7, "uppercase", uppercase), _defineProperty(_ref7, 'font-weight-normal', shouldUnbold({
    bold: bold,
    fontFamily: fontFamily,
    component: Component
  })), _ref7)]), useTypeStyles(), {
    className: className,
    style: style
  }), {
    // @ts-expect-error -- the `ref` is typed as `HTMLSpanElement`, but the `ref`
    // prop on `<Component>` could be either `HTMLSpanElement` or
    // `HTMLParagraphElement`. These types don't really matter internally;
    // what matters is that the external types behave well for consumers,
    // and that's what our types give us.
    ref: ref
  }, otherProps), /*#__PURE__*/React.createElement(TypographyContextProvider, {
    inline: true,
    bold: bold,
    size: size,
    fontFamily: fontFamily
  }, children));
});
TypeImpl.displayName = 'TypeImpl';